import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../../../components/dashboard/DashboardHeader";
import TaskSummaryCards from "../../../components/dashboard/TaskSummaryCards";
import DashboardAttentionAlerts from "../../../components/dashboard/DashboardAttentionAlerts";
import DashboardTrendCharts from "../../../components/dashboard/DashboardTrendCharts";
import TaskSummaryTable from "../../../components/dashboard/TaskSummaryTable";
import TatOverduePanel from "../../../components/dashboard/TatOverduePanel";
import MyTasksTable from "../../../components/dashboard/MyTasksTable";
import taskSummaryData from "../../../data/dashboard/taskSummary.json"; // kept for HOD fallback
import attentionAlertsData from "../../../data/dashboard/attentionAlerts.json";
import dashboardHeaderData from "../../../data/dashboard/dashboardHeader.json";
import completionTrendData from "../../../data/dashboard/completionTrend.json";
import overdueTrendData from "../../../data/dashboard/overdueTrend.json";
import tatByPersonData from "../../../data/dashboard/tatByPerson.json";
import departmentSummaryData from "../../../data/dashboard/departmentSummary.json";
import departmentTrendData from "../../../data/dashboard/departmentTrendData.json";
import DepartmentSummaryTable from "../../../components/dashboard/DepartmentSummaryTable";
import { getDashboardKpis, getDepartments, getCompletionTrend, getOverdueTrend, getTeamSummary, getDashboardTasks, getTatSummary, getAttentionItems, getOverdueTasks, getDeptSummary, getSubDeptSummary, getSubDeptMembers } from "../../../api/dashboardApi";
import "./Dashboard.css";

// ── Team view option definitions ─────────────────────────────────────────────
const ALL_TEAM_OPTIONS = [
  { value: "ic", label: "Individual Contributor" },
  { value: "tl", label: "Team Lead" },
  { value: "hod", label: "HOD" },
];

/**
 * Derives the allowed team-view options from the logged-in user object.
 *
 * Priority:
 *  1. If user.role is a non-empty array  → use those role values (filtered against ALL_TEAM_OPTIONS)
 *  2. Else if subordinatesCount.l1 > 0 || l2 > 0 → ['ic', 'tl']
 *  3. Else → ['ic']
 */
const deriveTeamOptions = (user) => {
  if (!user) return [ALL_TEAM_OPTIONS[0]]; // fallback: IC only

  if (user.business_role) {
    const roleValue = String(user.business_role).toLowerCase();
    if (roleValue === "hod") {
      return ALL_TEAM_OPTIONS;
    } else if (roleValue === "tl" || roleValue === "manager") {
      return ALL_TEAM_OPTIONS.filter((o) => o.value === "ic" || o.value === "tl");
    } else if (roleValue === "ic") {
      return [ALL_TEAM_OPTIONS[0]];
    }
  }

  const roles = Array.isArray(user.role) ? user.role : [];
  if (roles.length > 0) {
    const filtered = ALL_TEAM_OPTIONS.filter((opt) =>
      roles.map((r) => String(r).toLowerCase()).includes(opt.value)
    );
    return filtered.length > 0 ? filtered : [ALL_TEAM_OPTIONS[0]];
  }

  const sub = user.subordinatesCount ?? {};
  if ((sub.l1 ?? 0) > 0 || (sub.l2 ?? 0) > 0) {
    return ALL_TEAM_OPTIONS.filter((o) => o.value === "ic" || o.value === "tl");
  }

  return [ALL_TEAM_OPTIONS[0]]; // IC only
};

// cls values from API → valueTone used by TaskSummaryCards
const CLS_TO_TONE = {
  "kpi-red": "danger",
  "kpi-amber": "warning",
  "kpi-blue": "info",
  "kpi-green": "success",
};

// ── KPI card mapping ──────────────────────────────────────────────────────────
// API returns an array: [{label, value, sub, cls}, ...]
const mapKpisToCards = (kpis) =>
  Array.isArray(kpis)
    ? kpis.map((item, i) => ({
      key: item.label?.toLowerCase().replace(/\s+/g, "-") ?? `kpi-${i}`,
      label: item.label ?? "",
      value: item.value ?? "—",
      subtitle: item.sub ?? "",
      valueTone: CLS_TO_TONE[item.cls] ?? undefined,
    }))
    : [];

const SKELETON_CARDS = Array.from({ length: 5 }, (_, i) => ({
  key: `skeleton-${i}`,
  label: "Loading…",
  value: "—",
  subtitle: "",
}));

const DASHBOARD_TABS = [
  { id: "task-summary", label: "Task Summary" },
  { id: "tat-overdue", label: "TAT & Overdue" },
];

const Dashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const teamOptions = useMemo(() => deriveTeamOptions(user), [user]);

  const [activeTab, setActiveTab] = useState("task-summary");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("all");
  const [timePeriod, setTimePeriod] = useState({ timeRange: "this_month" });

  const [teamView, setTeamView] = useState(() => {
    return teamOptions[teamOptions.length - 1]?.value ?? "ic";
  });

  // If available team options change (e.g. after user loads), ensure teamView is still valid
  useEffect(() => {
    const isCurrentValid = teamOptions.some((o) => o.value === teamView);

    if (!isCurrentValid) {
      setTeamView(teamOptions[teamOptions.length - 1]?.value ?? "ic");
    }
  }, [teamOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to construct timeRange/startDate/endDate query parameters
  const getTimeParams = useCallback((tp) => {
    const timeRange = tp?.timeRange ?? tp ?? "this_month";
    const dateType = tp?.dateType ?? "due_date";
    return {
      timeRange,
      checked_by: dateType,
      ...(timeRange === "date_range" && tp?.startDate ? { startDate: tp.startDate, endDate: tp.endDate } : {}),
    };
  }, []);

  // Helper to build departments filter param (HOD only, when a specific dept is selected)
  const deptParam = useMemo(() => {
    if (teamView === "hod" && selectedDepartmentId && selectedDepartmentId !== "all") {
      return { departments: selectedDepartmentId };
    }
    return {};
  }, [teamView, selectedDepartmentId]);


  // ── Departments fetch ────────────────────────────────────────────────────────
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (teamView !== "hod") return;
    let cancelled = false;

    getDepartments({ role: teamView, ...getTimeParams(timePeriod) })
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          // Normalise to {id, label} shape; API may return {id, name} or {id, label}
          const mapped = data.map((d) => ({
            id: d.id ?? d.department_id ?? d.departmentId,
            label: d.label ?? d.name ?? d.DepartmentName ?? "",
          }));
          // Prepend an "All" chip that counts real departments
          setDepartments([
            { id: "all", label: `All (${mapped.length} dept${mapped.length !== 1 ? "s" : ""})` },
            ...mapped,
          ]);
          setSelectedDepartmentId("all");
        }
      })
      .catch(() => {
        if (!cancelled) setDepartments([]);
      });

    return () => {
      cancelled = true;
    };
  }, [teamView, timePeriod, getTimeParams]);

  // ── KPI fetch ───────────────────────────────────────────────────────────────
  const [kpiCards, setKpiCards] = useState(SKELETON_CARDS);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [kpiError, setKpiError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setKpiLoading(true);
    setKpiError(null);

    getDashboardKpis({ role: teamView, ...getTimeParams(timePeriod), ...deptParam })
      .then((data) => {
        if (!cancelled) {
          setKpiCards(mapKpisToCards(data));
          setKpiLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setKpiError(err.message || "Failed to load KPIs");
          setKpiLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [timePeriod, teamView, getTimeParams, deptParam]);

  // ── Attention Alerts fetch ──────────────────────────────────────────────────
  const [attentionItems] = useState([]);
  const [attentionLoading] = useState(false);

  /*
  useEffect(() => {
    let cancelled = false;
    setAttentionLoading(true);

    getAttentionItems({ role: teamView, ...getTimeParams(timePeriod) })
      .then((data) => {
        if (!cancelled) {
          const items = Array.isArray(data)
            ? data.map((d, i) => ({
              id: d.id ?? `attention-${i}`,
              tone: d.type ?? "warning",
              statusLabel: d.badge ?? "ALERT",
              person: d.person ?? "",
              task: d.task ?? "",
              message: d.note ?? "",
              metadata: Array.isArray(d.metadata) ? d.metadata : [],
              showReassign: d.showReassign ?? false,
            }))
            : [];
          setAttentionItems(items);
          setAttentionLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAttentionItems([]);
          setAttentionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [teamView, timePeriod, getTimeParams]);
  */

  // ── Completion trend fetch ────────────────────────────────────────────────────────
  // API: { date, created, completedSameDay } → chart: { period, inflow, outflow }
  const [completionPoints, setCompletionPoints] = useState([]);

  useEffect(() => {
    let cancelled = false;

    getCompletionTrend({ role: teamView, ...getTimeParams(timePeriod), ...deptParam })
      .then((data) => {
        if (!cancelled) {
          const points = Array.isArray(data)
            ? data.map((d) => ({
              period: d.period ?? d.date ?? "",
              inflow: d.inflow ?? d.created ?? 0,
              outflow: d.outflow ?? d.completedSameDay ?? 0,
            }))
            : [];
          setCompletionPoints(points);
        }
      })
      .catch(() => {
        if (!cancelled) setCompletionPoints([]);
      });

    return () => {
      cancelled = true;
    };
  }, [teamView, timePeriod, getTimeParams, deptParam]);

  // ── Overdue trend fetch ───────────────────────────────────────────────────────────
  // API: { person|name|department, completed, overdue, tatCount|tat_count } → chart reads same
  const [overdueRows, setOverdueRows] = useState([]);

  useEffect(() => {
    let cancelled = false;

    getOverdueTrend({ role: teamView, ...getTimeParams(timePeriod), ...deptParam })
      .then((data) => {
        if (!cancelled) {
          const rows = Array.isArray(data)
            ? data.map((d) => ({
              person: d.person ?? d.name ?? d.assignee ?? "",
              department: d.department ?? d.DepartmentName ?? d.name ?? "",
              completed: d.completed ?? d.completedCount ?? 0,
              overdue: d.overdue ?? d.overdueCount ?? 0,
              tatCount: d.tatCount ?? d.tat_count ?? 0,
            }))
            : [];
          setOverdueRows(rows);
        }
      })
      .catch(() => {
        if (!cancelled) setOverdueRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [teamView, timePeriod, getTimeParams, deptParam]);

  // ── Department Summary fetch (HOD only) ───────────────────────────────────────────────────
  const [deptSummaryRows, setDeptSummaryRows] = useState([]);
  const [deptSummaryLoading, setDeptSummaryLoading] = useState(false);
  const [expandedDeptRows, setExpandedDeptRows] = useState({});
  const [loadingRowIds, setLoadingRowIds] = useState(new Set());
  const [deptSorting, setDeptSorting] = useState([]);

  const normDeptRow = useCallback((d, i, parentId = "") => {
    const rawId = d.id ?? d.sub_department_id ?? d.subDepartmentId ?? d.user_id ?? d.userId ?? d.department_id ?? d.departmentId ?? `idx-${i}`;
    return {
      id: parentId ? `${parentId}-${rawId}` : rawId,
      rawId,
      department: d.name ?? d.sub_department_name ?? d.department_name ?? d.DepartmentName ?? d.department ?? "",
      subLabel: d.subLabel ?? (d.teamCount != null ? `${d.teamCount} team${d.teamCount !== 1 ? "s" : ""}` : undefined),
    assigned: d.assigned ?? d.open ?? 0,
    wip: d.wip ?? 0,
    completed: d.completed ?? d.done ?? 0,
    overdue: d.overdue ?? 0,
    completionPct: d.completionPct ?? d.completion ?? 0,
    overduePct: d.overduePct ?? 0,
    tatAdherencePct: d.tatAdherencePct ?? d.tatPct ?? 0,
    avgOverdueDays: parseFloat(d.avgOverdueDays ?? d.avgLate ?? 0),
    avgHrsPerTask: parseFloat(d.avgHrs ?? d.avgHrsPerTask ?? 0),
    openAgeAvgDays: d.openAge ?? d.openAgeAvgDays ?? 0,
  };
  }, []);

  useEffect(() => {
    if (teamView !== "hod") return;
    let cancelled = false;
    setDeptSummaryLoading(true);
    setExpandedDeptRows({});
    setLoadingRowIds(new Set());

    const deptSort = deptSorting[0];
    getDeptSummary({ 
      role: teamView, 
      ...getTimeParams(timePeriod), 
      ...deptParam,
      ...(deptSort ? { sortKey: deptSort.id, sortOrder: deptSort.desc ? 'desc' : 'asc' } : {}) 
    })
      .then((data) => {
        if (cancelled) return;
        const depts = Array.isArray(data) ? data : [];
        const rows = depts.map((d, i) => {
          const row = normDeptRow(d, i);
          const hasData = Number(row.assigned) > 0 || Number(row.wip) > 0 || Number(row.completed) > 0 || Number(row.overdue) > 0;
          return {
            ...row,
            id: `dept-${row.rawId}`,
            originalId: row.rawId,
            type: 'department',
            canExpand: hasData,
          };
        });
        setDeptSummaryRows(rows);
        setDeptSummaryLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setDeptSummaryRows([]);
          setDeptSummaryLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [teamView, timePeriod, deptParam, getTimeParams, normDeptRow, deptSorting]);

  useEffect(() => {
    const expandedIds = Object.keys(expandedDeptRows).filter((id) => expandedDeptRows[id]);
    if (expandedIds.length === 0) return;

    const findRow = (rows, targetId) => {
      for (let r of rows) {
        if (r.id === targetId) return r;
        if (r.subRows) {
          const found = findRow(r.subRows, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const fetchPromises = [];
    const newLoadingIds = [];

    expandedIds.forEach((id) => {
      if (loadingRowIds.has(id)) return;

      const row = findRow(deptSummaryRows, id);
      if (row && row.subRows === undefined) {
        newLoadingIds.push(id);

        const deptSort = deptSorting[0];
        if (row.type === 'department') {
          fetchPromises.push(
            getSubDeptSummary(row.originalId, { 
              role: teamView, 
              ...getTimeParams(timePeriod),
              ...(deptSort ? { sortKey: deptSort.id, sortOrder: deptSort.desc ? 'desc' : 'asc' } : {}) 
            })
              .then((data) => {
                const subData = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
                if (subData.length === 0) {
                  // Direct fetch members if no sub-departments exist
                  return getSubDeptMembers(`no-sub-${row.originalId}`, {
                    role: teamView, 
                    ...getTimeParams(timePeriod),
                    ...(deptSort ? { sortKey: deptSort.id, sortOrder: deptSort.desc ? 'desc' : 'asc' } : {}) 
                  })
                    .then((memData) => ({ id, data: memData, type: 'member' }))
                    .catch(() => ({ id, data: [], type: 'member' }));
                }
                return { id, data: subData, type: 'sub-department' };
              })
              .catch(() => ({ id, data: [], type: 'sub-department' }))
          );
        } else if (row.type === 'sub-department') {
          fetchPromises.push(
            getSubDeptMembers(row.originalId, { 
              role: teamView, 
              ...getTimeParams(timePeriod),
              ...(deptSort ? { sortKey: deptSort.id, sortOrder: deptSort.desc ? 'desc' : 'asc' } : {}) 
            })
              .then((data) => ({ id, data, type: 'member' }))
              .catch(() => ({ id, data: [], type: 'member' }))
          );
        }
      }
    });

    if (newLoadingIds.length > 0) {
      setLoadingRowIds((prev) => {
        const next = new Set(prev);
        newLoadingIds.forEach(id => next.add(id));
        return next;
      });

      setDeptSummaryRows((prevRows) => {
        const nextRows = JSON.parse(JSON.stringify(prevRows));
        const setTempLoading = (rows, targetId) => {
          for (let r of rows) {
            if (r.id === targetId) {
              r.subRows = [{ 
                id: `loading-${targetId}`, 
                department: "Loading...", 
                subLabel: "fetching data",
                canExpand: false,
                assigned: "", wip: "", completed: "", overdue: "", 
                completionPct: "", overduePct: "", tatAdherencePct: "", 
                avgOverdueDays: "", avgHrsPerTask: "", openAgeAvgDays: "" 
              }];
              return true;
            }
            if (r.subRows && setTempLoading(r.subRows, targetId)) return true;
          }
          return false;
        };
        newLoadingIds.forEach((id) => setTempLoading(nextRows, id));
        return nextRows;
      });
    }

    if (fetchPromises.length > 0) {
      Promise.all(fetchPromises).then((results) => {
        setDeptSummaryRows((prevRows) => {
          const nextRows = JSON.parse(JSON.stringify(prevRows));

          const updateSubRows = (rows, targetId, subRowsData, targetType) => {
            for (let r of rows) {
              if (r.id === targetId) {
                if (!subRowsData || subRowsData.length === 0) {
                  r.canExpand = false;
                  r.subRows = [];
                  return true;
                }

                r.subRows = subRowsData.map((d, i) => {
                  const norm = normDeptRow(d, i, targetId);
                  if (targetType === 'sub-department') {
                    norm.subLabel = norm.subLabel ?? (d.memberCount != null ? `${d.memberCount} member${d.memberCount !== 1 ? "s" : ""}` : undefined);
                  } else {
                    norm.department = d.name ?? d.person ?? d.email ?? `Member ${i + 1}`;
                    norm.subLabel = undefined;
                  }
                  const hasData = Number(norm.assigned) > 0 || Number(norm.wip) > 0 || Number(norm.completed) > 0 || Number(norm.overdue) > 0;
                  return {
                    ...norm,
                    id: `${targetType}-${norm.id}`,
                    originalId: norm.rawId,
                    type: targetType,
                    canExpand: targetType === 'sub-department' && hasData,
                  };
                });
                return true;
              }
              if (r.subRows) {
                if (updateSubRows(r.subRows, targetId, subRowsData, targetType)) return true;
              }
            }
            return false;
          };

          results.forEach(({ id, data, type }) => {
            const subData = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
            updateSubRows(nextRows, id, subData, type);
          });
          return nextRows;
        });

        setLoadingRowIds((prev) => {
          const next = new Set(prev);
          results.forEach((r) => next.delete(r.id));
          return next;
        });
      });
    }
  }, [expandedDeptRows, deptSummaryRows, loadingRowIds, teamView, timePeriod, getTimeParams, normDeptRow, deptSorting]);

  // ── Team summary fetch (Task Summary table) ──────────────────────────────────────────────
  const [teamRows, setTeamRows] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamPage, setTeamPage] = useState(1);
  const [teamLimit] = useState(10);
  const [teamTotal, setTeamTotal] = useState(0);
  const [teamSorting, setTeamSorting] = useState([]);

  useEffect(() => {
    if (teamView === "ic") return;
    let cancelled = false;
    setTeamLoading(true);

    const teamSort = teamSorting[0];
    getTeamSummary({
      role: teamView,
      page: teamPage,
      limit: teamLimit,
      ...getTimeParams(timePeriod),
      ...deptParam,
      ...(teamSort ? { sortKey: teamSort.id, sortOrder: teamSort.desc ? 'desc' : 'asc' } : {})
    })
      .then((data) => {
        if (!cancelled) {
          const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
          setTeamTotal(data?.pagination?.totalItems ?? items.length);
          setTeamRows(items.map((d, i) => ({
            id: d.user_id ?? d.id ?? d.userId ?? `row-${i}`,
            person: d.name ?? d.person ?? "",
            open: d.open ?? 0,
            wip: d.wip ?? 0,
            done: d.done ?? 0,
            overdue: d.overdue ?? 0,
            completionPct: d.completionPct ?? d.completion ?? 0,
            overduePct: d.overduePct ?? 0,
            avgHrsPerTask: parseFloat(d.avgHrs ?? d.avgHrsPerTask ?? 0),
            openAgeAvgDays: d.openAge ?? d.openAgeAvgDays ?? 0,
          })));
          setTeamLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTeamRows([]);
          setTeamLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [teamView, teamPage, teamLimit, timePeriod, getTimeParams, deptParam, teamSorting]);

  // ── TAT fetch (TatByPersonChart) ─────────────────────────────────────────────
  const [tatRows, setTatRows] = useState([]);
  const [tatLoading, setTatLoading] = useState(false);
  const [tatPage, setTatPage] = useState(1);
  const [tatLimit] = useState(10);
  const [tatTotal, setTatTotal] = useState(0);

  useEffect(() => {
    if (teamView === "ic") return;
    let cancelled = false;
    setTatLoading(true);

    getTatSummary({ role: teamView, page: tatPage, limit: tatLimit, ...getTimeParams(timePeriod), ...deptParam })
      .then((data) => {
        if (!cancelled) {
          const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
          setTatTotal(data?.pagination?.totalItems ?? items.length);
          setTatRows(items.map((d, i) => ({
            id: d.user_id ?? d.id ?? d.userId ?? `tat-${i}`,
            person: d.name ?? d.person ?? "",
            onTime: d.onTime ?? 0,
            late: d.late ?? 0,
            onTimePct: d.tatPct ?? d.onTimePct ?? 0,
            avgDaysLate: d.avgLate ?? "0d",
          })));
          setTatLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTatRows([]);
          setTatLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [teamView, tatPage, tatLimit, timePeriod, getTimeParams, deptParam]);

  // ── Overdue task rows fetch (TL/HOD TAT panel — lazy, tab-triggered) ────────────────────
  const [overdueTaskRows, setOverdueTaskRows] = useState([]);
  const [overdueTasksLoading, setOverdueTasksLoading] = useState(false);
  const [overdueTaskPage, setOverdueTaskPage] = useState(1);
  const [overdueTaskLimit] = useState(10);
  const [overdueTaskTotal, setOverdueTaskTotal] = useState(0);
  const [overdueTaskSortBy, setOverdueTaskSortBy] = useState("ageing");
  const [overdueTaskSortOrder, setOverdueTaskSortOrder] = useState("DESC");

  // Reset all paginations when department filter changes
  useEffect(() => {
    setTeamPage(1);
    setTatPage(1);
    setOverdueTaskPage(1);
  }, [selectedDepartmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (teamView === "ic" || activeTab !== "tat-overdue") return;
    let cancelled = false;
    setOverdueTasksLoading(true);

    getOverdueTasks({ 
      page: overdueTaskPage, 
      limit: overdueTaskLimit, 
      sortBy: overdueTaskSortBy,
      sortOrder: overdueTaskSortOrder,
      ...getTimeParams(timePeriod), 
      ...deptParam 
    })
      .then((data) => {
        if (!cancelled) {
          const tasks = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
          setOverdueTaskTotal(data?.pagination?.totalItems ?? tasks.length);
          setOverdueTaskRows(
            tasks.map((t) => {
              const fn = t.assignee?.first_name ?? "";
              const ln = t.assignee?.last_name ?? "";
              const owner = `${fn} ${ln}`.trim() || t.assignee?.email || "";
              const ageingDays = t.ageing ? Math.round(t.ageing / (60 * 24)) : 0;
              let daysOverdue = 0;
              if (t.due_date) {
                const diff = Date.now() - new Date(t.due_date).getTime();
                if (diff > 0) daysOverdue = Math.floor(diff / (1000 * 60 * 60 * 24));
              }
              return {
                id: t.id,
                task: t.title ?? "",
                owner,
                tag: t.project?.name ?? t.tag ?? "",
                priority: t.priority ?? "",
                dueDate: t.due_date ? new Date(t.due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "",
                daysOverdue,
                taskAge: ageingDays,
                hours: Math.round((t.logged_time ?? t.estimated_time ?? 0) / 3600),
              };
            })
          );
          setOverdueTasksLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOverdueTaskRows([]);
          setOverdueTasksLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [teamView, activeTab, overdueTaskPage, overdueTaskLimit, overdueTaskSortBy, overdueTaskSortOrder, timePeriod, getTimeParams, deptParam]);

  // ── My Tasks fetch (IC only) ────────────────────────────────────────────────────────────
  const [myTaskItems, setMyTaskItems] = useState([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [myTaskPage, setMyTaskPage] = useState(1);
  const [myTaskLimit, setMyTaskLimit] = useState(10);
  const [myTaskPagination, setMyTaskPagination] = useState(null);
  const [myTaskSorting, setMyTaskSorting] = useState([]);

  useEffect(() => {
    if (teamView !== "ic") return;
    let cancelled = false;
    setMyTasksLoading(true);

    const taskSort = myTaskSorting[0];
    getDashboardTasks({ 
      role: teamView, 
      page: myTaskPage, 
      limit: myTaskLimit, 
      ...getTimeParams(timePeriod),
      ...(taskSort ? { sortKey: taskSort.id, sortOrder: taskSort.desc ? 'desc' : 'asc' } : {}) 
    })
      .then((data) => {
        if (!cancelled) {
          setMyTaskItems(Array.isArray(data?.items) ? data.items : []);
          setMyTaskPagination(data?.pagination ?? null);
          setMyTasksLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMyTaskItems([]);
          setMyTasksLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [teamView, myTaskPage, myTaskLimit, timePeriod, getTimeParams, myTaskSorting]);

  const handlePersonClick = useCallback((personInfo, openInNewTab = false) => {
    const userId = personInfo?.originalId || personInfo?.id;
    if (!userId || String(userId).startsWith("row-") || String(userId).startsWith("tat-") || String(userId).startsWith("dept-") || String(userId).startsWith("sub-department-")) return;
    const filterId = personInfo?.isDepartment ? "department" : "assignedTo";
    const filters = [{ id: filterId, value: userId, label: personInfo?.person || personInfo?.department || personInfo?.name || "Member" }];
    const filtersString = JSON.stringify(filters);
    const url = `/mytask?tab=team&page=1&filters=${encodeURIComponent(filtersString)}`;
    if (openInNewTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      navigate(url);
    }
  }, [navigate]);

  const handleTaskClick = useCallback((taskId) => {
    if (!taskId) return;
    navigate(`/mytask?search=${encodeURIComponent(taskId)}`);
  }, [navigate]);

  const handleAlertAction = useCallback(() => { }, []);

  return (
    <div className="dashboard-page">
      <DashboardHeader
        title={dashboardHeaderData.title}
        subtitle=""//{dashboardHeaderData.subtitle}
        departments={departments}
        selectedDepartmentId={selectedDepartmentId}
        onDepartmentSelect={setSelectedDepartmentId}
        timePeriod={timePeriod}
        onTimePeriodChange={setTimePeriod}
        teamOptions={teamOptions}
        teamView={teamView}
        onTeamViewChange={setTeamView}
        onExportClick={handleAlertAction}
      />

      <div className="dashboard-summary-cards">
        {kpiError ? (
          <p className="dashboard-kpi-error">{kpiError}</p>
        ) : (
          <TaskSummaryCards cards={kpiCards} loading={kpiLoading} />
        )}
      </div>

      {/* IC: show My Tasks table directly, no tabs */}
      {teamView === "ic" ? (
        <div className="dashboard-panel">
          <div className="dashboard-tabs" role="tablist" aria-label="Dashboard views">
            <span className="dashboard-tab dashboard-tab--active">My Tasks</span>
          </div>
          <div className="dashboard-panel-content">
            <MyTasksTable
              rows={myTaskItems}
              isLoading={myTasksLoading}
              totalItems={myTaskPagination?.totalItems ?? 0}
              currentPage={myTaskPage}
              pageSize={myTaskLimit}
              onPaginationChange={({ page, limit }) => {
                setMyTaskPage(page);
                setMyTaskLimit(limit);
              }}
              sorting={myTaskSorting}
              onSortingChange={setMyTaskSorting}
            />
          </div>
        </div>
      ) : (
        <div className="dashboard-panel">
          <div className="dashboard-tabs" role="tablist" aria-label="Dashboard views">
            {DASHBOARD_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`dashboard-tab${activeTab === tab.id ? " dashboard-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.id === "task-summary" && teamView === "hod"
                  ? "Department Summary"
                  : tab.label}
              </button>
            ))}
          </div>

          <div className="dashboard-panel-content">
            {activeTab === "task-summary" ? (
              teamView === "hod" ? (
                <DepartmentSummaryTable 
                  rows={deptSummaryRows} 
                  isLoading={deptSummaryLoading} 
                  expanded={expandedDeptRows}
                  onExpandedChange={setExpandedDeptRows}
                  sorting={deptSorting}
                  onSortingChange={setDeptSorting}
                  onPersonClick={handlePersonClick}
                />
              ) : (
                <TaskSummaryTable
                  rows={teamRows}
                  onPersonClick={handlePersonClick}
                  isLoading={teamLoading}
                  totalItems={teamTotal}
                  currentPage={teamPage}
                  pageSize={teamLimit}
                  onPaginationChange={({ page }) => setTeamPage(page)}
                  sorting={teamSorting}
                  onSortingChange={setTeamSorting}
                />
              )
            ) : (
              <TatOverduePanel
                tatByPersonRows={tatRows}
                tatLoading={tatLoading}
                tatTotal={tatTotal}
                tatPage={tatPage}
                tatPageSize={tatLimit}
                onTatPaginationChange={({ page }) => setTatPage(page)}
                overdueTaskRows={overdueTaskRows}
                isLoading={overdueTasksLoading}
                totalItems={overdueTaskTotal}
                currentPage={overdueTaskPage}
                pageSize={overdueTaskLimit}
                onPaginationChange={({ page }) => setOverdueTaskPage(page)}
                onPersonClick={handlePersonClick}
                onSortChange={({ sortBy, sortOrder }) => {
                  setOverdueTaskSortBy(sortBy);
                  setOverdueTaskSortOrder(sortOrder);
                }}
                sortBy={overdueTaskSortBy}
                sortOrder={overdueTaskSortOrder}
                teamView={teamView}
              />
            )}
          </div>
        </div>
      )}

      {/*
      <DashboardAttentionAlerts
        items={attentionItems}
        totalCount={attentionItems.length}
        isLoading={attentionLoading}
        onFollowUp={handleAlertAction}
        onReassign={handleAlertAction}
        onPersonClick={handlePersonClick}
        onShowMore={handleAlertAction}
      />
      */}

      <div className="dashboard-charts-section">
        <DashboardTrendCharts
          completionPoints={completionPoints}
          overdueRows={overdueRows}
          xAxisKey={teamView === "hod" ? "department" : "person"}
          teamView={teamView}
        />
      </div>
    </div>
  );
};

export default memo(Dashboard);

